/**
 * Resize an image keeping aspect ratio
 * @param file - Image file
 * @param maxWidth - Maximum width (default: 800px)
 * @param maxHeight - Maximum height (default: 800px)
 * @param quality - JPEG/WEBP quality from 0 to 1 (default: 0.8)
 * @param convertToWebP - If true, converts the image to WebP before base64 (default: false)
 * @returns Promise with image in base64
 */
export const resizeImage = (
  file: File,
  maxWidth: number = 800,
  maxHeight: number = 800,
  quality: number = 0.8,
  convertToWebP: boolean = false
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = document.createElement("img");

    img.onload = () => {
      let { width, height } = img;

      // Calculate proportions using maxWidth and maxHeight
      const widthRatio = maxWidth / width;
      const heightRatio = maxHeight / height;
      const ratio = Math.min(widthRatio, heightRatio);

      // Keep original size if already smaller than max
      if (ratio < 1) {
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      // Setup canvas size
      canvas.width = width;
      canvas.height = height;

      if (!ctx) {
        reject(new Error("Could not create canvas context"));
        return;
      }

      // Draw the resized image
      ctx.drawImage(img, 0, 0, width, height);

      // Choose output format based on flag
      const mimeType = convertToWebP ? "image/webp" : "image/jpeg";

      // Convert to base64
      const base64 = canvas.toDataURL(mimeType, quality);

      URL.revokeObjectURL(img.src);
      resolve(base64);
    };

    img.onerror = (error) => {
      console.error("Error loading image:", error);
      URL.revokeObjectURL(img.src);
      reject(new Error("Error loading image"));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Valida se o arquivo é uma imagem válida
 * @param file - Arquivo para validar
 * @param maxSizeKB - Tamanho máximo em KB (padrão: 5mb)
 * @returns Objeto com resultado da validação
 */
export const validateImageFile = (file: File, maxSizeKB: number = 5120) => {
  const validTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/svg+xml",
  ];

  // Check if file type is supported
  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      error: "Unsupported file format. Use: JPG, PNG, GIF, WEBP or SVG.",
    };
  }

  // Convert KB to bytes
  const maxSizeBytes = maxSizeKB * 1024;

  // Check if file size is within limits
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File too large. Maximum allowed: ${maxSizeKB}KB.`,
    };
  }

  return { valid: true, error: null };
};

/**
 * Converte bytes para formato legível
 * @param bytes - Número de bytes
 * @returns String formatada (ex: "1.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Obtém informações de uma imagem
 * @param file - Arquivo de imagem
 * @returns Promise com informações da imagem
 */
export const getImageInfo = (
  file: File
): Promise<{
  width: number;
  height: number;
  size: string;
  type: string;
  name: string;
}> => {
  return new Promise((resolve, reject) => {
    const img = document.createElement("img");

    img.onload = () => {
      const info = {
        width: img.width,
        height: img.height,
        size: formatFileSize(file.size),
        type: file.type,
        name: file.name,
      };

      URL.revokeObjectURL(img.src);
      resolve(info);
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Erro ao carregar informações da imagem"));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Cria uma thumbnail quadrada cortando o centro da imagem
 * @param file - Arquivo de imagem
 * @param size - Tamanho da thumbnail (padrão: 200px)
 * @param quality - Qualidade JPEG de 0 a 1 (padrão: 0.8)
 * @returns Promise com a thumbnail em base64
 */
export const createSquareThumbnail = (
  file: File,
  size: number = 200,
  quality: number = 0.8
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = document.createElement("img");

    img.onload = () => {
      const { width, height } = img;

      // Configurar canvas como quadrado
      canvas.width = size;
      canvas.height = size;

      if (!ctx) {
        reject(new Error("Não foi possível criar o contexto do canvas"));
        return;
      }

      // Calcular área de corte (centro da imagem)
      const minDimension = Math.min(width, height);
      const cropX = (width - minDimension) / 2;
      const cropY = (height - minDimension) / 2;

      // Desenhar a imagem cortada e redimensionada
      ctx.drawImage(
        img,
        cropX,
        cropY,
        minDimension,
        minDimension, // Área de origem
        0,
        0,
        size,
        size // Área de destino
      );

      const base64 = canvas.toDataURL("image/jpeg", quality);
      URL.revokeObjectURL(img.src);
      resolve(base64);
    };

    img.onerror = (error) => {
      console.error("Erro ao carregar imagem:", error);
      URL.revokeObjectURL(img.src);
      reject(new Error("Erro ao criar thumbnail"));
    };

    img.src = URL.createObjectURL(file);
  });
};

export const getImageDimensions = (
  file: File
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load image for dimension detection"));
    };
    img.src = URL.createObjectURL(file);
  });
};
